import { useEffect, useMemo, useState } from 'react'
import { GROUP_ORDER } from '../api'
import type { AdminConfigListItem } from './api'
import { AdminAuthError, fetchConfigList } from './api'
import { ConfigEditor } from './ConfigEditor'

export default function ConfigsPane() {
  const [list, setList] = useState<AdminConfigListItem[] | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchConfigList()
      .then((r) => {
        setList(r.configs)
        setSelected(prev => prev ?? r.configs[0]?.slug ?? null)
      })
      .catch((err) => {
        if (err instanceof AdminAuthError) {
          window.location.reload()
          return
        }
        setError((err as Error).message)
      })
  }, [])

  const grouped = useMemo(() => {
    if (!list) return new Map<string, AdminConfigListItem[]>()
    const q = search.trim().toLowerCase()
    const groups = new Map<string, AdminConfigListItem[]>()
    for (const cat of GROUP_ORDER) groups.set(cat, [])
    for (const row of list) {
      if (q
        && !row.slug.toLowerCase().includes(q)
        && !row.displayName.toLowerCase().includes(q)) {
        continue
      }
      const k = (row.category || 'OTHER').toUpperCase()
      if (!groups.has(k)) groups.set(k, [])
      groups.get(k)!.push(row)
    }
    for (const [k, v] of groups) {
      if (v.length === 0) groups.delete(k)
      else v.sort((a, b) => a.displayName.localeCompare(b.displayName))
    }
    return groups
  }, [list, search])

  return (
    <div className="ad-pane">
      <aside className="ad-list-pane">
        <div className="ad-list-search">
          <span className="b-mono-dim">/</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="filter configs…"
            className="ad-list-search-input"
            spellCheck={false}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="ad-list-search-clear"
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        {error
          ? <p className="ad-msg is-err">Failed to load configs: {error}</p>
          : !list
              ? <p className="b-mono-dim">LOADING…</p>
              : grouped.size === 0
                  ? <p className="b-mono-dim ad-list-empty-search">no matches</p>
                  : [...grouped.entries()].map(([cat, rows]) => (
                      <div key={cat} className="ad-list-group is-open">
                        <div className="ad-list-group-head ad-list-group-head--static" aria-hidden="true">
                          <span className="ad-list-group-label">{cat}</span>
                          <span className="ad-list-group-count">{rows.length}</span>
                        </div>
                        {rows.map((r) => {
                          const active = r.slug === selected
                          return (
                            <button
                              type="button"
                              key={r.slug}
                              onClick={() => setSelected(r.slug)}
                              className={`ad-list-row${active ? ' is-active' : ''}`}
                            >
                              <span className="ad-list-name">
                                {r.displayName}
                                <span className="b-mono-dim ad-list-slug"> · {r.slug}</span>
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    ))}
      </aside>
      <div className="ad-edit-pane">
        {selected
          ? <ConfigEditor key={selected} slug={selected} />
          : <div className="ad-msg">Select a config from the list</div>}
      </div>
    </div>
  )
}
